/**
 * useExamSecurity.ts
 * Custom hook for exam security monitoring and prevention
 */

import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';

// Local interface definition to avoid circular dependencies
interface ExamConfig {
  id: string;
  title: string;
  securityConfig: {
    tabRestriction: boolean;
    violationLimit: number;
    restrictionLevel: 'strict' | 'warning' | 'none';
  };
}

interface SecurityViolation {
  type: 'copy' | 'tab_switch' | 'right_click' | 'keyboard_shortcut';
  timestamp: number;
  description: string;
}

interface ExamSecurityState {
  violations: SecurityViolation[];
  violationCount: number;
  isTabActive: boolean;
  securityLevel: 'normal' | 'warning' | 'restricted';
}

const STORAGE_KEY = 'exam-security-violations';
const MAX_VIOLATIONS = 3;

export function useExamSecurity(
  onAutoSubmit?: () => void,
  examConfig?: ExamConfig,
  disabled = false
) {
  const [state, setState] = useState<ExamSecurityState>({
    violations: [],
    violationCount: 0,
    isTabActive: true,
    securityLevel: 'normal',
  });

  // Load violations from localStorage
  useEffect(() => {
    if (disabled) return;

    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const violations = JSON.parse(saved);
        setState(prev => ({
          ...prev,
          violations,
          violationCount: violations.length,
          securityLevel:
            violations.length >= 2
              ? 'warning'
              : violations.length >= MAX_VIOLATIONS
                ? 'restricted'
                : 'normal',
        }));
      } catch (error) {
        console.warn('Failed to load security violations:', error);
      }
    }
  }, [disabled]);

  // Add violation with debouncing to prevent spam
  const addViolation = useCallback(
    (type: SecurityViolation['type'], description: string) => {
      const violation: SecurityViolation = {
        type,
        timestamp: Date.now(),
        description,
      };

      setState(prev => {
        // Check if same violation type happened recently (within 5 seconds)
        const recentViolation = prev.violations.find(
          v => v.type === type && Date.now() - v.timestamp < 5000
        );

        if (recentViolation) {
          return prev; // Skip duplicate violations
        }

        const newViolations = [...prev.violations, violation];
        const newCount = newViolations.length;

        // Save to localStorage
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newViolations));

        // Show warning toast only for serious violations
        if (type === 'tab_switch' || type === 'keyboard_shortcut') {
          toast.warning(`Cảnh báo bảo mật: ${description}`, {
            duration: 3000,
            className: 'border-orange-500 bg-orange-50 text-orange-900',
          });
        }

        // Auto-submit if max violations reached
        if (newCount >= MAX_VIOLATIONS && onAutoSubmit) {
          setTimeout(() => {
            toast.error('Đã đạt giới hạn vi phạm. Tự động nộp bài.', {
              duration: 6000,
              className: 'border-red-600 bg-red-100 text-red-900',
            });
            onAutoSubmit();
          }, 2000);
        }

        return {
          ...prev,
          violations: newViolations,
          violationCount: newCount,
          securityLevel:
            newCount >= MAX_VIOLATIONS ? 'restricted' : newCount >= 2 ? 'warning' : 'normal',
        };
      });
    },
    [onAutoSubmit]
  );

  // Keyboard shortcut prevention (only block dangerous shortcuts)
  useEffect(() => {
    if (disabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const { ctrlKey, metaKey, key } = e;

      // Only block developer tools and dangerous shortcuts
      if (key === 'F12' || (ctrlKey && key === 'u') || (ctrlKey && e.shiftKey && key === 'I')) {
        e.preventDefault();
        addViolation('keyboard_shortcut', `Chặn truy cập công cụ phát triển`);
      }

      // Block print
      if ((ctrlKey || metaKey) && key === 'p') {
        e.preventDefault();
        addViolation('keyboard_shortcut', `Chặn in tài liệu`);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [addViolation, disabled]);

  // Check if tab restriction is enabled
  const shouldRestrictTabs = examConfig?.securityConfig?.tabRestriction === true;
  const violationLimit = examConfig?.securityConfig?.violationLimit || MAX_VIOLATIONS;

  // Tab switch monitoring (only if explicitly enabled and with debouncing)
  useEffect(() => {
    if (!shouldRestrictTabs || disabled) return;

    let tabSwitchTimeout: NodeJS.Timeout;

    const handleVisibilityChange = () => {
      const isHidden = document.hidden;

      setState(prev => ({ ...prev, isTabActive: !isHidden }));

      if (isHidden) {
        // Debounce tab switch detection - only trigger after 2 seconds
        tabSwitchTimeout = setTimeout(() => {
          addViolation('tab_switch', 'Rời khỏi tab thi - hoạt động được giám sát');
        }, 2000);
      } else {
        // Clear timeout if user returns quickly
        if (tabSwitchTimeout) {
          clearTimeout(tabSwitchTimeout);
        }
      }
    };

    const handleBlur = () => {
      setState(prev => ({ ...prev, isTabActive: false }));
    };

    const handleFocus = () => {
      setState(prev => ({ ...prev, isTabActive: true }));
      // Clear timeout when focus returns
      if (tabSwitchTimeout) {
        clearTimeout(tabSwitchTimeout);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      if (tabSwitchTimeout) {
        clearTimeout(tabSwitchTimeout);
      }
    };
  }, [addViolation, shouldRestrictTabs, disabled]);

  // Remove aggressive text selection and right-click blocking
  // These cause too many false positives during normal exam taking

  // Clear violations (for testing/reset)
  const clearViolations = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setState({
      violations: [],
      violationCount: 0,
      isTabActive: true,
      securityLevel: 'normal',
    });
  }, []);

  // Reset exam security with new session
  const resetExamSecurity = useCallback((examId?: string) => {
    const sessionId = `exam_${examId || 'default'}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Clear all security storage
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('current_exam_session');
    sessionStorage.removeItem('violation_count');
    sessionStorage.removeItem('violation_warnings');
    sessionStorage.removeItem('tab_switches');

    // Create clean session
    const cleanSession = {
      sessionId,
      violations: [],
      violationCount: 0,
      isRestricted: false,
      startedAt: Date.now(),
    };

    localStorage.setItem('current_exam_session', JSON.stringify(cleanSession));

    setState({
      violations: [],
      violationCount: 0,
      isTabActive: true,
      securityLevel: 'normal',
    });

    return cleanSession;
  }, []);

  return {
    ...state,
    clearViolations,
    resetExamSecurity,
    maxViolations: violationLimit,
    shouldRestrictTabs,
    restrictionLevel: examConfig?.securityConfig?.restrictionLevel || 'strict',
  };
}
